/**
 * Estimate Status Utilities
 * 
 * Centralizes estimate status handling across the app.
 * 
 * STATUS VALUES:
 * - "draft" - Initial state, can be edited
 * - "sent" - Sent to customer via email
 * - "approved" - Estimate is approved (either by contractor or customer acceptance)
 * - "rejected" - Estimate was rejected by contractor (after being sent)
 * - "accepted" - LEGACY: Customer accepted via public link (treat as "approved")
 * - "declined" - Customer declined via public link
 * - "converted" - Estimate has been converted to a Job or Invoice
 * - "void" - Estimate was voided/cancelled
 * 
 * IMPORTANT: "accepted" is a LEGACY status that should be treated as "approved".
 * New customer acceptances now set status to "approved" directly.
 * Both statuses enable the same actions (Convert to Job, Convert to Invoice).
 */

/**
 * Normalize legacy "accepted" status to "approved"
 * @param {string} status - The estimate status
 * @returns {string} - Normalized status
 */
export const normalizeStatus = (status) => {
  // Treat legacy "accepted" as "approved"
  if (status === "accepted") return "approved";
  return status;
};

/**
 * Check if an estimate status allows conversion to Job/Invoice
 * @param {string} status - The estimate status
 * @returns {boolean}
 */
export const isConvertibleStatus = (status) => {
  const normalized = normalizeStatus(status);
  return normalized === "approved";
};

/**
 * Check if an estimate can be converted to a Job
 * @param {object} estimate - The estimate object
 * @returns {boolean}
 */
export const canConvertToJob = (estimate) => {
  if (!estimate) return false;
  return isConvertibleStatus(estimate.status) && !estimate.job_id;
};

/**
 * Check if an estimate can be converted to an Invoice
 * @param {object} estimate - The estimate object
 * @returns {boolean}
 */
export const canConvertToInvoice = (estimate) => {
  if (!estimate) return false;
  return isConvertibleStatus(estimate.status) && !estimate.job_id;
};

/**
 * Check if an estimate is in draft status
 * @param {string} status - The estimate status
 * @returns {boolean}
 */
export const isDraftStatus = (status) => {
  return status === "draft";
};

/**
 * Check if an estimate can be edited
 * Editable in both draft and sent status (sent will reset to draft on save)
 * @param {string} status - The estimate status
 * @returns {boolean}
 */
export const canEdit = (status) => {
  return status === "draft" || status === "sent";
};

/**
 * Check if an estimate can be sent/resent to customer
 * @param {string} status - The estimate status
 * @returns {boolean}
 */
export const canSendToCustomer = (status) => {
  return status === "draft" || status === "sent";
};

/**
 * Check if an estimate can be approved by contractor
 * @param {string} status - The estimate status
 * @returns {boolean}
 */
export const canApprove = (status) => {
  return status === "draft" || status === "sent";
};

/**
 * Check if an estimate can be rejected by contractor
 * Can reject in both draft and sent status
 * @param {string} status - The estimate status
 * @returns {boolean}
 */
export const canReject = (status) => {
  return status === "draft" || status === "sent";
};

/**
 * Get display label for estimate status
 * @param {string} status - The estimate status
 * @returns {string}
 */
export const getStatusLabel = (status) => {
  const labels = {
    draft: "Draft",
    sent: "Sent",
    approved: "Approved",
    rejected: "Rejected",
    accepted: "Approved", // Normalize legacy "accepted" to show as "Approved"
    declined: "Declined",
    converted: "Converted",
    void: "Void",
  };
  return labels[status] || status;
};

/**
 * Get CSS classes for status badge styling
 * Both "approved" and legacy "accepted" use the same green styling
 * @param {string} status - The estimate status
 * @returns {string}
 */
export const getStatusColor = (status) => {
  const colors = {
    draft: "bg-zinc-100 text-zinc-700 border-zinc-300",
    sent: "bg-blue-50 text-blue-700 border-blue-300",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-300",
    rejected: "bg-red-50 text-red-700 border-red-300",
    accepted: "bg-emerald-50 text-emerald-700 border-emerald-300", // Same as approved
    declined: "bg-red-50 text-red-700 border-red-300",
    converted: "bg-purple-50 text-purple-700 border-purple-300",
    void: "bg-zinc-200 text-zinc-500 border-zinc-400",
  };
  return colors[status] || colors.draft;
};

export default {
  normalizeStatus,
  isConvertibleStatus,
  canConvertToJob,
  canConvertToInvoice,
  isDraftStatus,
  canEdit,
  canSendToCustomer,
  canApprove,
  canReject,
  getStatusLabel,
  getStatusColor,
};
